import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ArrowRight, BookOpen, Cpu, TrendingUp, Users, Activity } from 'lucide-react';

interface Post {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  created_at: string;
  type: 'blog' | 'case_study';
}

export default function HomeBlogSection() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPosts() {
      const { data, error } = await supabase
        .from('cms_posts')
        .select('id, slug, title, excerpt, category, author, created_at, type')
        .eq('type', 'blog')
        .order('created_at', { ascending: false })
        .limit(3);

      if (!error && data) {
        setPosts(data);
      }
      setLoading(false);
    }
    fetchPosts();
  }, []);

  const getIcon = (category: string) => {
    const c = category.toLowerCase();
    if (c.includes('tech')) return Cpu;
    if (c.includes('productivity')) return TrendingUp;
    return Users;
  };

  if (loading || posts.length === 0) return null;

  return (
    <section className="py-24 bg-transparent relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/10 text-teal-400 text-[10px] font-bold uppercase tracking-[0.2em] rounded-full border border-teal-500/20 mb-4 font-mono">
              <BookOpen className="w-3 h-3" />
              Latest Insights
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tighter uppercase italic flex items-center gap-4">
              The <span className="text-teal-400">Pulse</span>
              <Activity className="w-8 h-8 text-teal-400 animate-pulse" />
            </h2>
          </div>
          <Link 
            to="/blog" 
            className="group flex items-center gap-2 text-slate-400 hover:text-white transition-colors font-bold uppercase tracking-widest text-xs"
          >
            Explore All Articles
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-teal-500" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.map((post) => {
            const Icon = getIcon(post.category);
            return (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group relative bg-slate-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-teal-500/50 transition-all duration-500 hover:-translate-y-2 flex flex-col shadow-2xl"
              >
                <div className="w-12 h-12 rounded-2xl bg-teal-500/10 flex items-center justify-center mb-6 border border-teal-500/20 group-hover:scale-110 group-hover:bg-teal-500/20 transition-all">
                  <Icon className="w-6 h-6 text-teal-400" />
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <span className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                    {new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors uppercase tracking-tight leading-tight line-clamp-2">
                  {post.title}
                </h3>
                
                <p className="text-slate-400 text-sm leading-relaxed mb-8 flex-1 font-medium italic line-clamp-3">
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between pt-6 border-t border-white/5 mt-auto">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">By {post.author}</span>
                  <ArrowRight className="w-5 h-5 text-teal-500 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
